import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { Timeline } from 'primereact/timeline';
import { getComplaintQuickView, ComplaintQuickViewResponse } from '../../services/order/order.service';
import './RegisterComplaintDialog.css';


interface ComplaintQuickViewDialogProps {
    visible: boolean;
    complaintId: number | null;
    onHide: () => void;
    fetchDetails?: (id: number) => Promise<ComplaintQuickViewResponse>;
    isCustomerView?: boolean;
    showInternalFields?: boolean;
}

const ComplaintQuickViewDialog: React.FC<ComplaintQuickViewDialogProps> = ({ visible, complaintId, onHide, fetchDetails, isCustomerView = false, showInternalFields = false }) => {
    const [loading, setLoading] = useState(false);
    const [complaint, setComplaint] = useState<ComplaintQuickViewResponse | null>(null);

    useEffect(() => {
        if (visible && complaintId) {
            loadComplaintDetails();
        }
    }, [visible, complaintId]);

    const loadComplaintDetails = async () => {
        if (!complaintId) return;

        setLoading(true);
        try {
            const data = fetchDetails
                ? await fetchDetails(complaintId)
                : await getComplaintQuickView(complaintId);
                console.log('Loaded complaint details:', data);

            setComplaint(data);
        } catch (error) {
            console.error('Error loading complaint details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
          // Treat as UTC if no timezone specified
        const dateStr = (dateString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString))
            ? dateString
            : `${dateString}Z`;

        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
        });
    };

    const getSeverityBadge = (priority: string) => {
        const styles: any = {
            HIGH: { bg: '#FEE2E2', text: '#DC2626', border: '#FECACA' },
            MEDIUM: { bg: '#FED7AA', text: '#EA580C', border: '#FDBA74' },
            LOW: { bg: '#D1FAE5', text: '#059669', border: '#A7F3D0' }
        };
        const style = styles[priority?.toUpperCase()] || { bg: '#F9FAFB', text: '#6B7280', border: '#E5E7EB' };

        return (
            <span
                className="px-3 py-1 border-round text-sm font-semibold"
                style={{
                    backgroundColor: style.bg,
                    color: style.text,
                    border: `1px solid ${style.border}`
                }}
            >
                {priority}
            </span>
        );
    };

    const getStatusBadge = (status: string) => {
        const styles: any = {
            NEW: { bg: '#E3F2FD', text: '#1E88E5', border: 'transparent' },
            'IN PROGRESS': { bg: '#FFF3E0', text: '#FB8C00', border: 'transparent' },
            RESOLVED: { bg: '#E8F5E9', text: '#43A047', border: 'transparent' },
            CLOSED: { bg: '#F5F5F5', text: '#757575', border: 'transparent' },
            REJECTED: { bg: '#FFEBEE', text: '#E53935', border: 'transparent' }
        };
        const style = styles[status?.toUpperCase()] || { bg: '#F5F5F5', text: '#757575', border: 'transparent' };

        return (
            <span
                className="px-3 py-1 border-round-2xl text-xs font-bold"
                style={{
                    backgroundColor: style.bg,
                    color: style.text,
                    letterSpacing: '0.5px'
                }}
            >
                {status}
            </span>
        );
    };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between pb-2 border-bottom-1 surface-border w-full">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-eye text-green-600 text-lg"></i>
                <span className="font-bold text-lg text-900">Complaint Details</span>
                <span className="text-500 text-sm ml-2">| CMP-{String(complaint?.id).padStart(3, '0')}</span>
            </div>
            <div className="mr-5">{/* Spacer for close button */}</div>
        </div>
    );

    return (
        <>

            <Dialog
                visible={visible}
                onHide={onHide}
                header={dialogHeader}
                style={{ width: '50vw' }}
                breakpoints={{ '960px': '75vw', '641px': '95vw' }}
                draggable={false}
                contentClassName="p-0 complaint-dialog-content-fix"
                className="complaint-dialog"
            >
                {loading ? (
                    <div className="flex justify-content-center align-items-center p-5">
                        <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".5s" />
                    </div>
                ) : complaint ? (
                    <div className="complaint-custom-scroll">
                        <div className="p-3">
                    {/* Top Row: Critical Info */}
                    <div className="grid p-fluid text-sm mb-3 px-3 pt-3">
                        {!isCustomerView && (
                            <div className="col-12 md:col-3">
                                <label className="text-500 text-xs block mb-1">Status</label>
                                <div>{getStatusBadge(complaint.statusName)}</div>
                            </div>
                        )}
                        <div className="col-12 md:col-3">
                            <label className="text-500 font-bold text-xs block mb-1">Type</label>
                            <span className="font-semibold text-900">{complaint.complaintTypeName}</span>
                        </div>
                        <div className="col-12 md:col-3">
                            <label className="text-500 font-bold text-xs block mb-1">Date</label>
                            <span className="text-900">{formatDateTime(complaint.createdDate)}</span>
                        </div>
                        <div className="col-12 md:col-3">
                            <label className="text-500 font-bold text-xs block mb-1">Created By</label>
                            <span className="text-900">{complaint.createdByName}</span>
                        </div>
                    </div>

                    <div className="px-3 pb-3">
                        {/* Description */}
                        <div className="dialog-section">
                            <div className="dialog-section-title">Description</div>
                            <p className="m-0 text-900 line-height-3 text-sm" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{complaint.description}</p>
                        </div>

                        {/* Product/Service Details */}
                        {complaint.productDetail && (
                            <div className="dialog-section">
                                <div className="dialog-section-title">Product Info</div>
                                <div className="grid text-sm m-0">
                                    <div className="col-6 p-1">
                                        <span className="text-500 mr-2">Product:</span>
                                        <span className="font-medium text-900">{complaint.productDetail.productName}</span>
                                    </div>
                                    <div className="col-6 p-1">
                                        <span className="text-500 mr-2">Invoice:</span>
                                        <span className="font-medium text-900">#{complaint.productDetail.invoiceNo}</span>
                                    </div>
                                    <div className="col-12 p-1">
                                        <span className="text-500 mr-2">Customer:</span>
                                        <span className="font-medium text-900">{complaint.productDetail.customerName}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions & Resolution - Conditionally Shown */}
                        {showInternalFields && (
                            <div className="dialog-section">
                                <div className="dialog-section-title">Resolution Details</div>
                                <div className="grid text-sm m-0">
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Action Description</span>
                                        <span className="font-medium text-900 block" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{complaint.actionDescription || '-'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Corrective Action</span>
                                        <span className="font-medium text-900 block" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{complaint.correctiveAction || '-'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Preventive Action</span>
                                        <span className="font-medium text-900 block" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{complaint.preventiveAction || '-'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Action Date</span>
                                        <span className="font-medium text-900">{formatDateTime(complaint.actionDate)}</span>
                                    </div>
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Action By</span>
                                        <span className="font-medium text-900">{complaint.actionByName || '-'}</span>
                                    </div>
                                    <div className="col-12 md:col-6 p-1">
                                        <span className="text-500 block mb-1">Cost Per Qty</span>
                                        <span className="font-medium text-900">
                                            {complaint.costPerQtyInRs != null ? `₹ ${complaint.costPerQtyInRs}` : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Attachments - List View with Download Icon */}
                        {complaint.attachments && complaint.attachments.length > 0 && (
                            <div className="dialog-section">
                                <div className="dialog-section-title">Attachments</div>
                                <ul className="list-none p-0 m-0">
                                    {complaint.attachments.map((attachment: any, index: number) => (
                                        <li key={index} className="flex align-items-center justify-content-between p-2 hover:surface-100 border-round transition-colors cursor-pointer mb-2 border-1 surface-border bg-white"
                                            onClick={() => window.open(attachment.fileUrl, '_blank')}>
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-file-pdf text-red-500 text-xl"></i>
                                                <span className="text-sm text-900 font-medium">{attachment.fileName}</span>
                                            </div>
                                            <Button icon="pi pi-download" size="small" aria-label="Download" />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Footer / Last Update */}
                        <div className="text-right text-xs text-500 mt-2">
                            Last updated by: <span className="font-medium text-700">{complaint.modifiedByName}</span> on {formatDateTime(complaint.modifiedDate)}
                        </div>
                    </div>
                    </div>
                    </div>
                ) : (
                    <div className="text-center p-3 text-500">No data</div>
                )}
            </Dialog>
        </>
    );
};

export default ComplaintQuickViewDialog;
